(() => {
  'use strict';

  if (window.__maraisSaleDiscoveryLoaded) return;
  window.__maraisSaleDiscoveryLoaded = true;

  const scopes = new Set(['all', 'women', 'men', 'kids', 'lifestyle']);
  const rootElement = () => document.querySelector('marais-sale-discovery');
  const parser = new DOMParser();
  let controller;
  let sequence = 0;
  let pendingURL;
  let committedControls;
  let committedNativeControls;
  let brandTypeahead = '';
  let brandTypeaheadTimer;

  const validScope = value => scopes.has(value) ? value : 'all';
  const withoutTechnicalParameters = url => {
    ['section_id', 'sections', 'sections_url', 'preview_theme_id'].forEach(key => url.searchParams.delete(key));
    return url;
  };

  function scopeFromURL(url, root) {
    if (url.searchParams.has('marais_scope')) return validScope(url.searchParams.get('marais_scope'));
    for (const scope of scopes) {
      const source = root.getAttribute('data-source-' + scope);
      if (source && new URL(source, location.origin).pathname === url.pathname) return scope;
    }
    return validScope(root.dataset.scope);
  }

  function canonicalURL(url, root, scope) {
    const next = withoutTechnicalParameters(new URL(url));
    const catalog = new URL(root.dataset.catalogRoot || root.dataset.sourceAll, location.origin);
    if (catalog.origin !== location.origin) throw new Error('invalid-catalog-origin');
    next.pathname = catalog.pathname;
    next.searchParams.set('view', 'marais-sale');
    next.searchParams.set('marais_scope', validScope(scope));
    return next;
  }

  function independentBrandURL(candidate, current, root, force = false) {
    const vendors = selectedVendorValues(candidate);
    const previous = selectedVendorValues(current);
    if (!force && vendors.slice().sort().join('\u001f') === previous.slice().sort().join('\u001f')) return candidate;
    const next = canonicalURL(candidate, root, 'all');
    Array.from(next.searchParams.keys()).forEach(key => {
      if (key.startsWith('filter.') && key !== 'filter.p.vendor' && key !== 'filter.v.availability') next.searchParams.delete(key);
    });
    ['page', 'q'].forEach(key => next.searchParams.delete(key));
    return next;
  }

  function controlsSnapshot(root) {
    return {
      brand: root.querySelector('[data-sale-brand]')?.value,
      sort: root.querySelector('[data-sale-sort]')?.value
    };
  }

  function selectedVendorValues(url) {
    return url.searchParams.getAll('filter.p.vendor').filter(Boolean);
  }

  function nativeVendorLabel(value) {
    const input = Array.from(document.querySelectorAll('form[is="facet-form"] input[name="filter.p.vendor"]')).find(element => element.value === value);
    const label = input?.id ? document.querySelector('label[for="' + CSS.escape(input.id) + '"]') : null;
    return label?.textContent.trim() || value;
  }

  function brandMenuParts(root = rootElement()) {
    return {
      root,
      select: root?.querySelector('[data-sale-brand]'),
      trigger: root?.querySelector('[data-sale-brand-toggle]'),
      menu: root?.querySelector('[data-sale-brand-menu]'),
      options: root?.querySelector('[data-sale-brand-options]'),
      label: root?.querySelector('[data-sale-brand-label]')
    };
  }

  function initializeBrandMenu(root, url) {
    const { select, trigger, menu, options, label } = brandMenuParts(root);
    if (!select) return;
    try {
      const vendors = JSON.parse(select.dataset.saleAllVendors || '[]');
      if (Array.isArray(vendors)) vendors.forEach(vendor => {
        if (typeof vendor === 'string' && vendor && !Array.from(select.options).some(option => option.value === vendor)) select.append(new Option(vendor, vendor));
      });
    } catch {}
    const values = selectedVendorValues(url);
    if (values.length === 0) select.value = '';
    else if (values.length === 1) {
      let option = Array.from(select.options).find(item => item.value === values[0]);
      if (!option) {
        option = new Option(values[0].startsWith('gid://') ? nativeVendorLabel(values[0]) : values[0], values[0]);
        option.setAttribute('data-sale-brand-legacy', '');
        select.append(option);
      }
      select.value = values[0];
    } else {
      let option = Array.from(select.options).find(item => item.value === '__multiple');
      if (!option) {
        option = new Option('', '__multiple');
        option.disabled = true;
        select.append(option);
      }
      option.textContent = values.map(value => Array.from(select.options).find(item => item.value === value)?.textContent || nativeVendorLabel(value)).join(', ');
      select.value = '__multiple';
    }
    if (label) label.textContent = select.selectedOptions[0]?.textContent || select.options[0]?.textContent || '';
    if (trigger) trigger.setAttribute('aria-label', [select.getAttribute('aria-label'), label?.textContent].filter(Boolean).join(': '));
    if (!trigger || !menu || !options) return;
    if (typeof menu.showPopover !== 'function') {
      trigger.hidden = true;
      select.hidden = false;
      select.removeAttribute('tabindex');
      return;
    }
    const fragment = document.createDocumentFragment();
    Array.from(select.options).forEach(option => {
      if (option.value === '__multiple' || (option.hasAttribute('data-sale-brand-legacy') && !values.includes(option.value))) return;
      const choice = document.createElement('button');
      choice.type = 'button';
      choice.setAttribute('data-sale-brand-choice', option.value);
      choice.setAttribute('role', 'menuitemradio');
      choice.setAttribute('aria-checked', String(option.value === '' ? values.length === 0 : values.includes(option.value)));
      choice.tabIndex = -1;
      choice.disabled = option.disabled;
      choice.textContent = option.textContent;
      fragment.append(choice);
    });
    options.replaceChildren(fragment);
    trigger.setAttribute('aria-expanded', String(menu.matches(':popover-open')));
  }

  function syncNativeVendorChoices(root, url) {
    const select = root?.querySelector('[data-sale-brand]');
    if (!select) return;
    const values = selectedVendorValues(url);
    document.querySelectorAll('form[is="facet-form"]').forEach((form, formIndex) => {
      form.querySelectorAll('[data-sale-preserved-vendor]').forEach(element => element.remove());
      const nativeInputs = Array.from(form.querySelectorAll('input[name="filter.p.vendor"][type="checkbox"]'));
      const exemplar = nativeInputs[0];
      if (!exemplar) return;
      const list = exemplar.closest('.checkbox-list');
      if (!list) return;
      values.forEach((value, index) => {
        if (nativeInputs.some(input => input.value === value)) return;
        const exemplarWrapper = exemplar.closest('.checkbox-control') || exemplar.parentElement;
        let wrapper = exemplarWrapper.cloneNode(true);
        let input = wrapper.querySelector('input[name="filter.p.vendor"]');
        let label = wrapper.querySelector('label');
        if (!input || !label || exemplarWrapper === list || wrapper.tagName === 'LABEL') {
          wrapper = document.createElement('div');
          wrapper.className = 'checkbox-control';
          input = exemplar.cloneNode(true);
          const nativeLabel = exemplar.id ? form.querySelector('label[for="' + CSS.escape(exemplar.id) + '"]') : null;
          label = nativeLabel ? nativeLabel.cloneNode(false) : document.createElement('label');
          wrapper.append(input, label);
        }
        const id = (exemplar.id || 'sale-brand') + '-individual-' + formIndex + '-' + index;
        wrapper.setAttribute('data-sale-preserved-vendor', value);
        wrapper.removeAttribute('id');
        input.id = id;
        input.value = value;
        input.checked = true;
        input.defaultChecked = true;
        input.disabled = false;
        input.removeAttribute('disabled');
        label.htmlFor = id;
        label.textContent = Array.from(select.options).find(option => option.value === value)?.textContent || value;
        list.prepend(wrapper);
      });
    });
  }

  function positionBrandMenu() {
    const { trigger, menu } = brandMenuParts();
    if (!trigger || !menu || !menu.matches(':popover-open')) return;
    const rect = trigger.getBoundingClientRect();
    const width = Math.min(264, window.innerWidth - 36);
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const viewportTop = window.visualViewport?.offsetTop || 0;
    const below = viewportTop + viewportHeight - rect.bottom - 26;
    const above = rect.top - viewportTop - 26;
    const openAbove = below < Math.min(176, viewportHeight * .45) && above > below;
    const maxHeight = Math.max(44, Math.min(320, viewportHeight * .45, openAbove ? above : below));
    menu.style.position = 'fixed';
    menu.style.inset = 'auto';
    menu.style.margin = '0';
    menu.style.width = width + 'px';
    menu.style.maxHeight = Math.floor(maxHeight) + 'px';
    menu.style.overflowY = 'auto';
    menu.style.left = Math.max(18, Math.min(rect.left, window.innerWidth - width - 18)) + 'px';
    const height = Math.min(menu.scrollHeight, maxHeight);
    menu.style.top = Math.max(viewportTop + 10, openAbove ? rect.top - height - 8 : rect.bottom + 8) + 'px';
  }

  function focusBrandChoice(choice) {
    const { menu } = brandMenuParts();
    if (!menu || !choice) return;
    menu.querySelectorAll('[data-sale-brand-choice]').forEach(button => button.tabIndex = button === choice ? 0 : -1);
    choice.focus({ preventScroll: true });
    const choiceRect = choice.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    if (choiceRect.top < menuRect.top) menu.scrollTop -= menuRect.top - choiceRect.top;
    if (choiceRect.bottom > menuRect.bottom) menu.scrollTop += choiceRect.bottom - menuRect.bottom;
  }

  function closeBrandMenu(restore = false) {
    const { trigger, menu } = brandMenuParts();
    if (menu?.matches(':popover-open')) menu.hidePopover();
    trigger?.setAttribute('aria-expanded', 'false');
    if (restore) trigger?.focus({ preventScroll: true });
  }

  function openBrandMenu(last = false) {
    const { trigger, menu } = brandMenuParts();
    if (!menu || typeof menu.showPopover !== 'function') return;
    if (!menu.matches(':popover-open')) menu.showPopover();
    trigger?.setAttribute('aria-expanded', 'true');
    positionBrandMenu();
    const choices = Array.from(menu.querySelectorAll('[data-sale-brand-choice]:not(:disabled)'));
    const selected = choices.find(choice => choice.getAttribute('aria-checked') === 'true');
    focusBrandChoice(last ? choices.at(-1) : selected || choices[0]);
  }

  function nativeControlsSnapshot(main) {
    return main ? Array.from(main.querySelectorAll('form[is="facet-form"] input, form[is="facet-form"] select')).map(element => ({
      id: element.id,
      name: element.name,
      type: element.type,
      value: element.value,
      checked: element.checked
    })) : [];
  }

  function restoreNativeControls(main) {
    if (!main || !committedNativeControls) return;
    const candidates = Array.from(main.querySelectorAll('form[is="facet-form"] input, form[is="facet-form"] select'));
    committedNativeControls.forEach(saved => {
      const element = saved.id ? document.getElementById(saved.id) : candidates.find(candidate => candidate.name === saved.name && candidate.type === saved.type && candidate.value === saved.value);
      if (!element || !main.contains(element)) return;
      if (saved.type === 'checkbox' || saved.type === 'radio') element.checked = saved.checked;
      else element.value = saved.value;
    });
  }

  function restoreControls(root) {
    if (!root || !committedControls) return;
    const brand = root.querySelector('[data-sale-brand]');
    const sort = root.querySelector('[data-sale-sort]');
    if (brand && committedControls.brand !== undefined) brand.value = committedControls.brand;
    if (sort && committedControls.sort !== undefined) sort.value = committedControls.sort;
    initializeBrandMenu(root, new URL(location.href));
    syncNativeVendorChoices(root, new URL(location.href));
  }

  function focusSnapshot(element) {
    if (!(element instanceof Element) || element === document.body) return null;
    for (const attribute of ['data-sale-breadcrumb', 'data-sale-scope', 'data-sale-brand-toggle', 'data-sale-brand', 'data-sale-sort', 'data-sale-sort-preset', 'data-sale-clear', 'data-sale-reset']) {
      if (element.hasAttribute(attribute)) return { attribute, value: element.getAttribute(attribute) };
    }
    if (element.id) return { id: element.id };
    if (element.getAttribute('name')) return { name: element.getAttribute('name'), value: element.value };
    const controlled = element.closest('[aria-controls]');
    return controlled ? { controls: controlled.getAttribute('aria-controls') } : null;
  }

  function restoreFocus(snapshot) {
    if (!snapshot) return;
    let target;
    if (snapshot.attribute) {
      target = document.querySelector('marais-sale-discovery [' + snapshot.attribute + '="' + CSS.escape(snapshot.value) + '"]');
    } else if (snapshot.id) {
      target = document.getElementById(snapshot.id);
    } else if (snapshot.name) {
      target = Array.from(document.getElementsByName(snapshot.name)).find(element => element.value === snapshot.value);
    } else if (snapshot.controls) {
      target = document.querySelector('[aria-controls="' + CSS.escape(snapshot.controls) + '"]');
    }
    if (target instanceof HTMLElement && !target.hasAttribute('disabled') && target.getClientRects().length) {
      target.focus({ preventScroll: true });
      return;
    }
    const fallback = rootElement()?.querySelector('[data-sale-controls]');
    if (fallback instanceof HTMLElement) {
      if (!fallback.hasAttribute('tabindex')) fallback.setAttribute('tabindex', '-1');
      fallback.focus({ preventScroll: true });
    }
  }

  function setBusy(root, main, busy, message) {
    if (root) {
      root.setAttribute('aria-busy', String(busy));
      const status = root.querySelector('[data-sale-status]');
      if (status && message !== undefined) {
        status.classList.remove('sr-only');
        status.textContent = message;
      }
    }
    if (main) {
      main.setAttribute('aria-busy', String(busy));
      main.inert = busy;
      main.querySelector('#collection-products')?.setAttribute('aria-busy', String(busy));
      main.classList.toggle('is-loading', busy);
    }
  }

  async function renderURL(candidate, { historyMode = 'push', trigger = document.activeElement } = {}) {
    const root = rootElement();
    if (!root) return;
    const main = document.getElementById('collection-products')?.closest('.shopify-section');
    const currentSequence = ++sequence;
    controller?.abort();
    controller = new AbortController();
    const signal = controller.signal;
    const focus = focusSnapshot(trigger);
    const focusAtStart = document.activeElement;
    const openDisclosures = main ? Array.from(main.querySelectorAll('details[open], facet-dialog[open]')).map(element => element.id).filter(Boolean) : [];
    const mainID = main?.id.replace(/^shopify-section-/, '');
    const introID = root.dataset.sectionId;
    const scope = scopeFromURL(candidate, root);
    let visible;

    try {
      if (!main || !mainID || !introID || mainID === introID) throw new Error('missing-section-context');
      visible = canonicalURL(candidate, root, scope);
      pendingURL = new URL(visible);
      const sourceValue = root.getAttribute('data-source-' + scope);
      if (!sourceValue) throw new Error('missing-scope-source');
      const endpoint = new URL(sourceValue, location.origin);
      if (endpoint.origin !== location.origin) throw new Error('invalid-source-origin');
      endpoint.search = visible.search;
      endpoint.hash = '';
      endpoint.searchParams.delete('marais_scope');
      endpoint.searchParams.set('sections', introID + ',' + mainID);
      if (root.dataset.previewTheme) endpoint.searchParams.set('preview_theme_id', root.dataset.previewTheme);
      setBusy(root, main, true, root.dataset.loading || '');
      const response = await fetch(endpoint.toString(), {
        signal,
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error('section-request-failed');
      const sections = await response.json();
      if (signal.aborted || currentSequence !== sequence) return;
      if (typeof sections[introID] !== 'string' || typeof sections[mainID] !== 'string' || !sections[introID].trim() || !sections[mainID].trim()) {
        throw new Error('missing-section-response');
      }
      const introDocument = parser.parseFromString(sections[introID], 'text/html');
      const mainDocument = parser.parseFromString(sections[mainID], 'text/html');
      const incomingIntro = introDocument.getElementById('shopify-section-' + introID);
      const incomingRoot = incomingIntro?.querySelector('marais-sale-discovery');
      const incomingMain = mainDocument.getElementById('shopify-section-' + mainID);
      if (!incomingRoot || incomingRoot.dataset.sectionId !== introID || !incomingMain || !incomingMain.querySelector('#collection-products')) {
        throw new Error('invalid-section-response');
      }
      if (validScope(incomingRoot.dataset.scope) !== scope) throw new Error('unexpected-scope-response');
      incomingMain.querySelectorAll('script').forEach(script => script.remove());
      incomingRoot.querySelectorAll('script').forEach(script => script.remove());
      openDisclosures.forEach(id => {
        const disclosure = incomingMain.querySelector('#' + CSS.escape(id));
        if (disclosure) disclosure.setAttribute('open', '');
      });
      const nextRoot = document.importNode(incomingRoot, true);
      const nextMain = document.importNode(incomingMain, true);
      const nextNodes = Array.from(nextMain.childNodes);
      if (signal.aborted || currentSequence !== sequence || !root.isConnected || !main.isConnected) return;
      const canRestoreFocus = document.activeElement === focusAtStart || document.activeElement === document.body || main.contains(document.activeElement) || root.contains(document.activeElement);
      const focusAfterCommit = document.activeElement !== focusAtStart && document.activeElement !== document.body ? focusSnapshot(document.activeElement) : focus;
      const previousMainNodes = Array.from(main.childNodes);
      try {
        main.replaceChildren(...nextNodes);
        root.replaceWith(nextRoot);
        setBusy(nextRoot, main, false, nextRoot.dataset.results || '');
        nextRoot.querySelector('[data-sale-status]')?.classList.add('sr-only');
        if (historyMode === 'push' && visible.href !== location.href) history.pushState({}, '', visible);
      } catch (error) {
        main.replaceChildren(...previousMainNodes);
        if (nextRoot.isConnected) nextRoot.replaceWith(root);
        throw error;
      }
      initializeBrandMenu(nextRoot, visible);
      syncNativeVendorChoices(nextRoot, visible);
      committedControls = controlsSnapshot(nextRoot);
      committedNativeControls = nativeControlsSnapshot(main);
      pendingURL = null;
      if (canRestoreFocus) restoreFocus(focusAfterCommit);
      const resultLabel = nextRoot.dataset.results;
      if (resultLabel) requestAnimationFrame(() => {
        if (currentSequence === sequence && nextRoot.isConnected) {
          const status = nextRoot.querySelector('[data-sale-status]');
          if (status) {
            status.classList.add('sr-only');
            status.textContent = resultLabel;
          }
        }
      });
      document.dispatchEvent(new CustomEvent('marais-sale:updated', { detail: { scope, url: visible.href } }));
    } catch (error) {
      if (signal.aborted || currentSequence !== sequence) return;
      console.warn('[Marais Sale]', error.message);
      pendingURL = null;
      const currentRoot = rootElement();
      restoreControls(currentRoot);
      restoreNativeControls(main);
      setBusy(currentRoot, main, false, currentRoot?.dataset.error || '');
      if (document.activeElement === focusAtStart || document.activeElement === document.body) restoreFocus(focus);
    }
  }

  function currentURL(root) {
    const url = pendingURL ? new URL(pendingURL) : new URL(location.href);
    return canonicalURL(url, root, scopeFromURL(url, root));
  }

  function changeDiscovery(changes, trigger) {
    const root = rootElement();
    if (!root) return;
    const url = currentURL(root);
    // Section buttons navigate to the whole audience, independently of current facets.
    if (Object.hasOwn(changes, 'marais_scope')) {
      Array.from(url.searchParams.keys()).forEach(key => {
        if (key.startsWith('filter.')) url.searchParams.delete(key);
      });
      url.searchParams.delete('q');
      url.searchParams.set('filter.v.availability', '1');
    }
    Object.entries(changes).forEach(([key, value]) => {
      url.searchParams.delete(key);
      if (value !== '' && value !== null) url.searchParams.set(key, value);
    });
    url.searchParams.delete('page');
    const next = Object.hasOwn(changes, 'filter.p.vendor') ? independentBrandURL(url, currentURL(root), root, true) : url;
    renderURL(next, { trigger });
  }

  function submitNativeForm(form, trigger) {
    const root = rootElement();
    if (!root || !(form instanceof HTMLFormElement)) return;
    const main = form.closest('.shopify-section') || document.getElementById('collection-products')?.closest('.shopify-section');
    if (main?.inert) return;
    const supplied = new URL(form.action, location.origin);
    if (supplied.origin !== location.origin) return;
    supplied.search = '';
    for (const [key, value] of new FormData(form)) {
      if (typeof value !== 'string') continue;
      if (value === '' && ['page', 'filter.v.price.gte', 'filter.v.price.lte'].includes(key)) continue;
      supplied.searchParams.append(key, value);
    }
    const current = currentURL(root);
    const url = independentBrandURL(canonicalURL(supplied, root, scopeFromURL(current, root)), current, root);
    url.hash = current.hash;
    renderURL(url, { trigger });
  }

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!rootElement() || !(form instanceof HTMLFormElement) || !form.matches('form[is="facet-form"]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    submitNativeForm(form, event.submitter || document.activeElement);
  }, true);

  document.addEventListener('facet:update', event => {
    const root = rootElement();
    if (!root) return;
    event.stopImmediatePropagation();
    let supplied;
    try { supplied = new URL(event.detail.url, location.origin); } catch { return; }
    if (supplied.origin !== location.origin) return;
    const current = currentURL(root);
    const scope = scopeFromURL(current, root);
    const url = independentBrandURL(canonicalURL(supplied, root, scope), current, root);
    url.hash = current.hash;
    renderURL(url);
  }, true);

  document.addEventListener('click', event => {
    const root = rootElement();
    if (!root || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target instanceof Element ? event.target.closest('[data-sale-breadcrumb], [data-sale-scope], [data-sale-sort-preset], [data-sale-clear], [data-sale-reset]') : null;
    if (!target || !root.contains(target)) return;
    event.preventDefault();
    if (target.hasAttribute('data-sale-breadcrumb')) {
      const url = new URL(target.href, location.origin);
      if (url.origin === location.origin) renderURL(url, { trigger: target });
    } else if (target.hasAttribute('data-sale-scope')) {
      changeDiscovery({ marais_scope: validScope(target.dataset.saleScope) }, target);
    } else if (target.hasAttribute('data-sale-sort-preset')) {
      changeDiscovery({ sort_by: target.dataset.saleSortPreset }, target);
    } else {
      const url = currentURL(root);
      if (target.dataset.saleClear && target.dataset.saleClear.startsWith('filter.')) {
        const parameter = target.dataset.saleClear;
        const value = target.getAttribute('data-sale-value');
        if (value !== null) {
          const remaining = url.searchParams.getAll(parameter).filter(entry => entry !== value);
          url.searchParams.delete(parameter);
          remaining.forEach(entry => url.searchParams.append(parameter, entry));
        } else {
          url.searchParams.delete(parameter);
        }
      } else {
        Array.from(url.searchParams.keys()).forEach(key => {
          if (key.startsWith('filter.')) url.searchParams.delete(key);
        });
        url.searchParams.set('filter.v.availability', '1');
      }
      url.searchParams.delete('page');
      renderURL(url, { trigger: target });
    }
  });

  document.addEventListener('change', event => {
    const root = rootElement();
    const target = event.target;
    if (!root || !(target instanceof HTMLSelectElement) || !root.contains(target)) return;
    if (target.hasAttribute('data-sale-brand')) {
      const label = root.querySelector('[data-sale-brand-label]');
      if (label) label.textContent = target.selectedOptions[0]?.textContent || '';
      root.querySelector('[data-sale-brand-toggle]')?.setAttribute('aria-label', [target.getAttribute('aria-label'), label?.textContent].filter(Boolean).join(': '));
      changeDiscovery({ 'filter.p.vendor': target.value }, root.querySelector('[data-sale-brand-toggle]') || target);
    }
    if (target.hasAttribute('data-sale-sort')) changeDiscovery({ sort_by: target.value }, target);
  });

  document.addEventListener('click', event => {
    const root = rootElement();
    const target = event.target instanceof Element ? event.target.closest('[data-sale-brand-toggle], [data-sale-brand-choice]') : null;
    if (!root || !target || !root.contains(target)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (target.hasAttribute('data-sale-brand-toggle')) {
      const menu = root.querySelector('[data-sale-brand-menu]');
      if (menu?.matches(':popover-open')) closeBrandMenu(true);
      else openBrandMenu();
      return;
    }
    if (target.disabled) return;
    const select = root.querySelector('[data-sale-brand]');
    if (!select) return;
    select.value = target.getAttribute('data-sale-brand-choice');
    closeBrandMenu(true);
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }, true);

  document.addEventListener('keydown', event => {
    const root = rootElement();
    if (!root || !(event.target instanceof Element)) return;
    const { trigger, menu } = brandMenuParts(root);
    if (event.target === trigger && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      openBrandMenu(event.key === 'ArrowUp');
      return;
    }
    if (!menu || !menu.matches(':popover-open') || !menu.contains(event.target)) return;
    const choices = Array.from(menu.querySelectorAll('[data-sale-brand-choice]:not(:disabled)'));
    const index = choices.indexOf(document.activeElement);
    if (event.key === 'Escape' || event.key === 'Tab') {
      if (event.key === 'Escape') event.preventDefault();
      closeBrandMenu(true);
      return;
    }
    if (event.key === ' ' || event.key === 'Enter') {
      const choice = document.activeElement;
      if (choice instanceof HTMLButtonElement && choice.hasAttribute('data-sale-brand-choice') && !choice.disabled) {
        event.preventDefault();
        choice.click();
      }
      return;
    }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? choices.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + choices.length) % choices.length;
      focusBrandChoice(choices[next]);
      return;
    }
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      brandTypeahead += event.key.toLocaleLowerCase();
      clearTimeout(brandTypeaheadTimer);
      brandTypeaheadTimer = setTimeout(() => brandTypeahead = '', 650);
      const ordered = choices.slice(index + 1).concat(choices.slice(0, index + 1));
      const match = ordered.find(choice => choice.textContent.trim().toLocaleLowerCase().startsWith(brandTypeahead));
      if (match) focusBrandChoice(match);
    }
  }, true);

  document.addEventListener('toggle', event => {
    if (!(event.target instanceof Element) || !event.target.matches('[data-sale-brand-menu]')) return;
    const { trigger, menu } = brandMenuParts();
    if (menu !== event.target) return;
    trigger?.setAttribute('aria-expanded', String(event.newState === 'open'));
    if (event.newState === 'open') positionBrandMenu();
  }, true);

  window.addEventListener('resize', positionBrandMenu);
  window.visualViewport?.addEventListener('resize', positionBrandMenu);
  window.visualViewport?.addEventListener('scroll', positionBrandMenu);
  document.addEventListener('scroll', positionBrandMenu, true);

  window.addEventListener('popstate', () => {
    if (rootElement()) renderURL(new URL(location.href), { historyMode: 'none' });
  });

  const root = rootElement();
  if (!root) return;
  initializeBrandMenu(root, new URL(location.href));
  syncNativeVendorChoices(root, new URL(location.href));
  committedControls = controlsSnapshot(root);
  committedNativeControls = nativeControlsSnapshot(document.getElementById('collection-products')?.closest('.shopify-section'));
  const url = new URL(location.href);
  if (scopeFromURL(url, root) !== validScope(root.dataset.scope)) renderURL(url, { historyMode: 'none' });
})();

